/**
 * BOM list + editor page.
 *
 * Mock-aware. The "New BOM" / "Edit" / "Activate" / "Clone as new version"
 * actions update the local mock state when running with mock data; they
 * also call the corresponding InstantDB transactions when wired up.
 */

import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  Boxes,
  CheckCircle2,
  Copy,
  Pencil,
  Plus,
} from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import {
  Badge,
  Button,
  DataTable,
  EmptyState,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  StatusPill,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  cn,
  toast,
} from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportFilename,
  formatDateForCsv,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  BomEditor,
  type BomLineDraft,
  type BomSubmitPayload,
} from "../../../components/forms/BomEditor";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import {
  mockBoms,
  mockBomLines,
  mockProducts,
  mockUnits,
  type MockBom,
  type MockBomLine,
  type MockProduct,
  type MockUnit,
} from "../../../lib/catalogMock";
import {
  mockStockMovements,
  type MockStockMovement,
} from "../../../lib/inventoryMock";
import { RecordAuditBadge } from "../../../components/audit/RecordAuditBadge";
import { RecordAuditTrail } from "../../../components/audit/RecordAuditTrail";

export const Route = createFileRoute("/_app/catalog/bom")({
  component: BomPage,
});

/* ─────────────────────────────────────────────────────────────
 * Helpers
 * ───────────────────────────────────────────────────────────── */

type ActiveFilter = "active" | "inactive" | "all";

function relativeTime(ms: number): string {
  const diff = Date.now() - ms;
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toLocaleDateString("en-BD");
}

function unitLabel(id: string, units: MockUnit[]): string {
  return units.find((u) => u._id === id)?.code ?? "—";
}

function unitDisplay(id: string, units: MockUnit[]): string {
  return units.find((u) => u._id === id)?.displayName ?? id;
}

function productLabel(
  id: string,
  products: MockProduct[],
): { sku: string; name: string } {
  const p = products.find((m) => m._id === id);
  return { sku: p?.sku ?? id, name: p?.displayName ?? "(unknown)" };
}

let __idCounter = 0;
function nextLocalId(prefix: string): string {
  __idCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${__idCounter}`;
}

function isoDate(ms: number): string {
  return new Date(ms).toISOString().slice(0, 10);
}

/* ─────────────────────────────────────────────────────────────
 * Page
 * ───────────────────────────────────────────────────────────── */

function BomPage() {
  const [boms, setBoms] = usePersistentMockState<MockBom[]>("boms", mockBoms);
  const [lines, setLines] = usePersistentMockState<MockBomLine[]>("bomLines", mockBomLines);
  const [products] = usePersistentMockState<MockProduct[]>(
    "products",
    mockProducts,
  );
  const [units] = usePersistentMockState<MockUnit[]>("units", mockUnits);
  const [stockMovements] = usePersistentMockState<MockStockMovement[]>(
    "stockMovements",
    mockStockMovements,
  );

  /** On-hand totals per product (sum of quantityDelta across all warehouses). */
  const onHandByProduct = useMemo(() => {
    const map = new Map<string, number>();
    for (const m of stockMovements) {
      const cur = map.get(m.productId) ?? 0;
      map.set(m.productId, cur + m.quantityDelta);
    }
    return map;
  }, [stockMovements]);

  const [filterActive, setFilterActive] = useState<ActiveFilter>("all");

  const filteredBoms = useMemo(() => {
    return boms
      .slice()
      .sort((a, b) => b.createdAt - a.createdAt)
      .filter((b) => {
        if (filterActive === "active") return b.isActive;
        if (filterActive === "inactive") return !b.isActive;
        return true;
      });
  }, [boms, filterActive]);

  const linesByBom = useMemo(() => {
    const map = new Map<string, MockBomLine[]>();
    for (const l of lines) {
      const list = map.get(l.bomId) ?? [];
      list.push(l);
      map.set(l.bomId, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.sortOrder - b.sortOrder);
    return map;
  }, [lines]);

  // Sheet state
  const [createOpen, setCreateOpen] = useState(false);
  const [detailBom, setDetailBom] = useState<MockBom | null>(null);
  const [editing, setEditing] = useState(false);

  const productOptions = useMemo(
    () =>
      products
        .filter((p) => p.productKind !== "service")
        .map((p) => ({
          id: p._id,
          label: `${p.sku} — ${p.displayName}`,
        })),
    [products],
  );
  const unitOptions = useMemo(
    () =>
      units.map((u) => ({
        id: u._id,
        label: `${u.displayName} (${u.code})`,
      })),
    [units],
  );
  const componentOptions = productOptions; // any non-service product can be a component

  /* Handlers */

  function handleCreate(payload: BomSubmitPayload) {
    // version label = vN — date
    const versionN =
      boms.filter((b) => b.productId === payload.productId).length + 1;
    const newBomId = nextLocalId("bom");
    const now = Date.now();
    const product = products.find((p) => p._id === payload.productId);
    const newBom: MockBom = {
      _id: newBomId,
      productId: payload.productId,
      productVariantId: null,
      code: `BOM-${product?.sku ?? "X"}-V${versionN}`,
      versionLabel: `v${versionN} — ${isoDate(now)}`,
      isActive: false,
      outputQuantity: payload.outputQuantity,
      outputUnitId: payload.outputUnitId,
      notes: payload.notes || null,
      activatedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    const newLines: MockBomLine[] = payload.lines.map((line, idx) => ({
      _id: nextLocalId("bln"),
      bomId: newBomId,
      componentProductId: line.componentProductId,
      componentVariantId: null,
      quantity: Number(line.quantityE6) / 1_000_000,
      unitId: line.unitId,
      wastageAllowanceE4: BigInt(line.scrapPercentBp * 100),
      notes: null,
      sortOrder: idx,
    }));
    setBoms((prev) => [newBom, ...prev]);
    setLines((prev) => [...prev, ...newLines]);
    toast.success(`Created BOM ${newBom.code} (inactive)`);
    setCreateOpen(false);
  }

  function handleEditExisting(bomId: string, payload: BomSubmitPayload) {
    const bom = boms.find((b) => b._id === bomId);
    if (!bom) return;
    if (bom.isActive) {
      toast.error("Active BOMs are read-only. Clone as new version first.");
      return;
    }
    const now = Date.now();
    setBoms((prev) =>
      prev.map((b) =>
        b._id === bomId
          ? {
              ...b,
              outputQuantity: payload.outputQuantity,
              outputUnitId: payload.outputUnitId,
              notes: payload.notes || null,
              updatedAt: now,
            }
          : b,
      ),
    );
    // Replace this BOM's lines with the submitted ones.
    setLines((prev) => [
      ...prev.filter((l) => l.bomId !== bomId),
      ...payload.lines.map((line, idx) => ({
        _id: nextLocalId("bln"),
        bomId,
        componentProductId: line.componentProductId,
        componentVariantId: null as string | null,
        quantity: Number(line.quantityE6) / 1_000_000,
        unitId: line.unitId,
        wastageAllowanceE4: BigInt(line.scrapPercentBp * 100),
        notes: null as string | null,
        sortOrder: idx,
      })),
    ]);
    toast.success(`Updated ${bom.code}. Audit trail recorded.`);
    setEditing(false);
  }

  function handleActivate(bom: MockBom) {
    if (bom.isActive) return;
    const now = Date.now();
    setBoms((prev) =>
      prev.map((b) => {
        if (b.productId !== bom.productId) return b;
        if (b._id === bom._id)
          return { ...b, isActive: true, activatedAt: now, updatedAt: now };
        if (b.isActive) return { ...b, isActive: false, updatedAt: now };
        return b;
      }),
    );
    toast.success(`Activated ${bom.code}`);
    setDetailBom((cur) =>
      cur && cur._id === bom._id
        ? { ...cur, isActive: true, activatedAt: now, updatedAt: now }
        : cur,
    );
  }

  function handleClone(source: MockBom) {
    const versionN =
      boms.filter((b) => b.productId === source.productId).length + 1;
    const newBomId = nextLocalId("bom");
    const now = Date.now();
    const product = products.find((p) => p._id === source.productId);
    const newBom: MockBom = {
      ...source,
      _id: newBomId,
      code: `BOM-${product?.sku ?? "X"}-V${versionN}`,
      versionLabel: `v${versionN} — ${isoDate(now)}`,
      isActive: false,
      activatedAt: null,
      createdAt: now,
      updatedAt: now,
    };
    const sourceLines = lines.filter((l) => l.bomId === source._id);
    const newLines: MockBomLine[] = sourceLines.map((l) => ({
      ...l,
      _id: nextLocalId("bln"),
      bomId: newBomId,
    }));
    setBoms((prev) => [newBom, ...prev]);
    setLines((prev) => [...prev, ...newLines]);
    toast.success(`Cloned ${source.code} → ${newBom.code}`);
    setDetailBom(newBom);
    setEditing(false);
  }

  /* Columns */

  const columns: ColumnDef<MockBom>[] = useMemo(
    () => [
      {
        id: "outputProduct",
        header: "Output Product",
        cell: ({ row }) => {
          const p = productLabel(row.original.productId, products);
          return (
            <div className="flex flex-col">
              <span className="font-medium text-(--text-primary)">{p.name}</span>
              <span className="font-mono text-[11px] text-(--text-tertiary)">
                {p.sku}
              </span>
            </div>
          );
        },
      },
      {
        id: "version",
        header: "Version",
        cell: ({ row }) => (
          <span className="font-mono text-[12px]">
            {row.original.versionLabel}
          </span>
        ),
        meta: { mono: true },
      },
      {
        id: "active",
        header: "Active",
        cell: ({ row }) => (
          <StatusPill variant={row.original.isActive ? "success" : "neutral"}>
            {row.original.isActive ? "Active" : "Draft"}
          </StatusPill>
        ),
      },
      {
        id: "componentsSummary",
        header: "Components",
        cell: ({ row }) => {
          const bomLines = linesByBom.get(row.original._id) ?? [];
          const total = bomLines.length;
          if (total === 0) {
            return (
              <span className="text-[12px] text-(--text-tertiary)">
                No components
              </span>
            );
          }
          let okCount = 0;
          let shortCount = 0;
          for (const l of bomLines) {
            const available = onHandByProduct.get(l.componentProductId) ?? 0;
            if (available >= l.quantity) okCount += 1;
            else shortCount += 1;
          }
          return (
            <div className="flex items-center gap-1.5 text-[12px]">
              <span className="font-mono">{total}</span>
              {shortCount > 0 ? (
                <StatusPill variant="danger">{shortCount} short</StatusPill>
              ) : (
                <StatusPill variant="success">All OK</StatusPill>
              )}
            </div>
          );
        },
      },
      {
        id: "updatedAt",
        header: "Updated",
        cell: ({ row }) => (
          <span className="text-[12px] text-(--text-tertiary)">
            {relativeTime(row.original.updatedAt)}
          </span>
        ),
        meta: { hideOnMobile: true },
      },
    ],
    [linesByBom, products, onHandByProduct],
  );

  function handleExportCsv(extension: "csv" | "xls") {
    // BOM × line export — one row per component line.
    type BomRow = {
      bomCode: string;
      bomVersion: string;
      productSku: string;
      productName: string;
      isActive: boolean;
      outputQuantity: number;
      outputUnit: string;
      lineNumber: number;
      componentSku: string;
      componentName: string;
      quantity: number;
      unit: string;
      wastagePercent: string;
      lineNotes: string;
      bomCreated: string;
    };
    const out: BomRow[] = [];
    for (const bom of filteredBoms) {
      const product = products.find((p) => p._id === bom.productId);
      const outputUnit = units.find((u) => u._id === bom.outputUnitId);
      const bomLines = linesByBom.get(bom._id) ?? [];
      if (bomLines.length === 0) {
        out.push({
          bomCode: bom.code,
          bomVersion: bom.versionLabel,
          productSku: product?.sku ?? "",
          productName: product?.displayName ?? "",
          isActive: bom.isActive,
          outputQuantity: bom.outputQuantity,
          outputUnit: outputUnit?.code ?? "",
          lineNumber: 0,
          componentSku: "",
          componentName: "",
          quantity: 0,
          unit: "",
          wastagePercent: "",
          lineNotes: "",
          bomCreated: formatDateForCsv(bom.createdAt),
        });
      } else {
        for (const line of bomLines) {
          const comp = products.find((p) => p._id === line.componentProductId);
          const lineUnit = units.find((u) => u._id === line.unitId);
          out.push({
            bomCode: bom.code,
            bomVersion: bom.versionLabel,
            productSku: product?.sku ?? "",
            productName: product?.displayName ?? "",
            isActive: bom.isActive,
            outputQuantity: bom.outputQuantity,
            outputUnit: outputUnit?.code ?? "",
            lineNumber: line.sortOrder,
            componentSku: comp?.sku ?? "",
            componentName: comp?.displayName ?? "",
            quantity: line.quantity,
            unit: lineUnit?.code ?? "",
            wastagePercent: (Number(line.wastageAllowanceE4) / 10000).toFixed(2),
            lineNotes: line.notes ?? "",
            bomCreated: formatDateForCsv(bom.createdAt),
          });
        }
      }
    }
    const columns: ExportColumn<BomRow>[] = [
      { key: "bomCode", label: "BOM" },
      { key: "bomVersion", label: "Version" },
      { key: "productSku", label: "Product SKU" },
      { key: "productName", label: "Product" },
      { key: "isActive", label: "Active" },
      { key: "outputQuantity", label: "Output qty" },
      { key: "outputUnit", label: "Output unit" },
      { key: "lineNumber", label: "Line #" },
      { key: "componentSku", label: "Component SKU" },
      { key: "componentName", label: "Component" },
      { key: "quantity", label: "Qty" },
      { key: "unit", label: "Unit" },
      { key: "wastagePercent", label: "Wastage %" },
      { key: "lineNotes", label: "Line notes" },
      { key: "bomCreated", label: "BOM created" },
    ];
    downloadCsv(exportFilename("boms", extension), toCsv(out, columns));
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Bill of Materials"
        description="Define recipes for manufactured products, including yields and scrap."
        actions={
          <>
            <ExportButton
              onCsv={() => handleExportCsv("csv")}
              onExcel={() => handleExportCsv("xls")}
            />
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" /> New BOM
            </Button>
          </>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary)">
          Filter
        </span>
        <div
          role="tablist"
          aria-label="Active filter"
          className="inline-flex rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-0.5"
        >
          {(["active", "inactive", "all"] as ActiveFilter[]).map((opt) => (
            <button
              key={opt}
              type="button"
              role="tab"
              aria-selected={filterActive === opt}
              onClick={() => setFilterActive(opt)}
              className={cn(
                "px-2.5 py-1 text-[12px] font-medium rounded transition-colors capitalize",
                filterActive === opt
                  ? "bg-(--bg-surface) text-(--text-primary) shadow-[var(--shadow-xs)]"
                  : "text-(--text-secondary) hover:text-(--text-primary)",
              )}
            >
              {opt}
            </button>
          ))}
        </div>
      </div>

      <DataTable
        data={filteredBoms}
        columns={columns}
        getRowId={(b) => b._id}
        onRowClick={(row) => {
          setDetailBom(row.original);
          setEditing(false);
        }}
        emptyTitle="No BOMs"
        emptyDescription="Connect a manufactured product to its inputs and outputs to see it here."
        emptyIcon={<Boxes className="h-5 w-5" aria-hidden />}
        emptyAction={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> New BOM
          </Button>
        }
      />

      {/* Create Sheet */}
      <Sheet open={createOpen} onOpenChange={setCreateOpen}>
        <SheetContent side="right" size="md">
          <SheetHeader>
            <SheetTitle>New BOM</SheetTitle>
            <SheetDescription>
              Created as a draft. Activate it once verified — that demotes any
              previous active BOM for the same product.
            </SheetDescription>
          </SheetHeader>
          <SheetBody>
            <BomEditor
              products={productOptions}
              components={componentOptions}
              units={unitOptions}
              onHandByProduct={onHandByProduct}
              submitLabel="Create BOM"
              onSubmit={handleCreate}
              onCancel={() => setCreateOpen(false)}
            />
          </SheetBody>
        </SheetContent>
      </Sheet>

      {/* Detail Sheet */}
      <Sheet
        open={detailBom !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDetailBom(null);
            setEditing(false);
          }
        }}
      >
        <SheetContent side="right" size="md">
          {detailBom ? (
            editing && !detailBom.isActive ? (
              <>
                <SheetHeader>
                  <SheetTitle>Edit {detailBom.code}</SheetTitle>
                  <SheetDescription>
                    Only inactive BOM versions are editable.
                  </SheetDescription>
                </SheetHeader>
                <SheetBody>
                  <BomEditor
                    productLocked
                    versionLabel={detailBom.versionLabel}
                    products={productOptions}
                    components={componentOptions}
                    units={unitOptions}
                    onHandByProduct={onHandByProduct}
                    submitLabel="Save changes"
                    initialValues={{
                      productId: detailBom.productId,
                      outputUnitId: detailBom.outputUnitId,
                      outputQuantity: detailBom.outputQuantity,
                      notes: detailBom.notes ?? "",
                      versionLabel: detailBom.versionLabel,
                      lines: (linesByBom.get(detailBom._id) ?? []).map(
                        (l, idx) => toLineDraft(l, idx),
                      ),
                    }}
                    onSubmit={(p) => handleEditExisting(detailBom._id, p)}
                    onCancel={() => setEditing(false)}
                  />
                </SheetBody>
              </>
            ) : (
              <BomDetailReadonly
                bom={detailBom}
                lines={linesByBom.get(detailBom._id) ?? []}
                onEdit={() => setEditing(true)}
                onActivate={() => handleActivate(detailBom)}
                onClone={() => handleClone(detailBom)}
                products={products}
                units={units}
                onHandByProduct={onHandByProduct}
              />
            )
          ) : (
            <EmptyState title="No BOM selected" />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function toLineDraft(line: MockBomLine, idx: number): BomLineDraft {
  return {
    uid: `existing_${line._id}`,
    serverId: line._id,
    componentProductId: line.componentProductId,
    quantityMajor: line.quantity.toString(),
    unitId: line.unitId,
    scrapPercent: (Number(line.wastageAllowanceE4) / 10_000).toString(),
    sortOrder: idx,
  };
}

function BomDetailReadonly({
  bom,
  lines,
  onEdit,
  onActivate,
  onClone,
  products,
  units,
  onHandByProduct,
}: {
  bom: MockBom;
  lines: MockBomLine[];
  onEdit: () => void;
  onActivate: () => void;
  onClone: () => void;
  products: MockProduct[];
  units: MockUnit[];
  onHandByProduct: Map<string, number>;
}) {
  const product = productLabel(bom.productId, products);

  // Availability summary
  const availabilitySummary = useMemo(() => {
    let ok = 0;
    let short = 0;
    for (const l of lines) {
      const available = onHandByProduct.get(l.componentProductId) ?? 0;
      if (available >= l.quantity) ok += 1;
      else short += 1;
    }
    return { ok, short, total: lines.length };
  }, [lines, onHandByProduct]);

  return (
    <>
      <SheetHeader>
        <SheetTitle className="flex items-center gap-2 flex-wrap">
          <span>{bom.code}</span>
          <Badge
            variant={bom.isActive ? "success" : "neutral"}
            className="font-mono"
          >
            {bom.versionLabel}
          </Badge>
          <RecordAuditBadge tableName="boms" recordId={bom._id} />
        </SheetTitle>
        <SheetDescription>
          {product.name} ({product.sku})
        </SheetDescription>
      </SheetHeader>
      <SheetBody>
        <Tabs defaultValue="overview">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          <TabsContent value="overview" className="mt-4">
            <div className="flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-3">
            <DetailItem label="Output">
              <span className="font-mono">
                {bom.outputQuantity} {unitLabel(bom.outputUnitId, units)}
              </span>
            </DetailItem>
            <DetailItem label="Status">
              <StatusPill variant={bom.isActive ? "success" : "neutral"}>
                {bom.isActive ? "Active" : "Draft / Historical"}
              </StatusPill>
            </DetailItem>
            <DetailItem label="Activated">
              {bom.activatedAt
                ? new Date(bom.activatedAt).toLocaleDateString("en-BD")
                : "—"}
            </DetailItem>
            <DetailItem label="Updated">{relativeTime(bom.updatedAt)}</DetailItem>
          </div>

          {/* Stock availability summary bar */}
          {lines.length > 0 ? (
            <div className="rounded-[var(--radius-md)] border border-(--border-subtle) bg-(--bg-canvas) px-4 py-3">
              <div className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary) mb-2">
                Stock Availability
              </div>
              <div className="flex items-center gap-4 text-[13px]">
                <span className="font-mono">
                  {availabilitySummary.total} components
                </span>
                <span className="inline-flex items-center gap-1">
                  <span className="inline-block h-2 w-2 rounded-full bg-(--success)" />
                  <span className="text-(--text-secondary)">
                    {availabilitySummary.ok} available
                  </span>
                </span>
                {availabilitySummary.short > 0 ? (
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block h-2 w-2 rounded-full bg-(--danger)" />
                    <span className="text-(--danger) font-medium">
                      {availabilitySummary.short} short
                    </span>
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          {bom.notes ? (
            <div>
              <div className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary) mb-1">
                Notes
              </div>
              <p className="text-[13px] text-(--text-primary) whitespace-pre-wrap">
                {bom.notes}
              </p>
            </div>
          ) : null}

          <div>
            <div className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary) mb-2">
              Components ({lines.length})
            </div>
            <div className="rounded-md border border-(--border-subtle) overflow-hidden">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="text-left text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary) bg-(--bg-canvas)">
                    <th className="px-3 py-2">Component</th>
                    <th className="px-3 py-2 text-right">Required</th>
                    <th className="px-3 py-2 text-right">Available</th>
                    <th className="px-3 py-2">Unit</th>
                    <th className="px-3 py-2 text-right">Scrap</th>
                    <th className="px-3 py-2 text-center">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-3 py-6 text-center text-(--text-tertiary)"
                      >
                        No components
                      </td>
                    </tr>
                  ) : (
                    lines.map((l) => {
                      const c = productLabel(l.componentProductId, products);
                      const scrapPct =
                        Number(l.wastageAllowanceE4) / 10_000;
                      const available = onHandByProduct.get(l.componentProductId) ?? 0;
                      const isShort = available < l.quantity;
                      return (
                        <tr
                          key={l._id}
                          className="border-t border-(--border-subtle)"
                        >
                          <td className="px-3 py-2">
                            <div className="flex flex-col">
                              <span className="text-(--text-primary)">
                                {c.name}
                              </span>
                              <span className="font-mono text-[11px] text-(--text-tertiary)">
                                {c.sku}
                              </span>
                            </div>
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">
                            {l.quantity}
                          </td>
                          <td
                            className={cn(
                              "px-3 py-2 text-right font-mono tabular-nums",
                              isShort
                                ? "text-(--danger) font-medium"
                                : "text-(--success)",
                            )}
                          >
                            {available}
                          </td>
                          <td className="px-3 py-2 text-(--text-secondary)">
                            {unitDisplay(l.unitId, units)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono tabular-nums">
                            {scrapPct.toFixed(2)}%
                          </td>
                          <td className="px-3 py-2 text-center">
                            <StatusPill
                              variant={isShort ? "danger" : "success"}
                            >
                              {isShort ? "Short" : "OK"}
                            </StatusPill>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
            </div>
          </TabsContent>
          <TabsContent value="history" className="mt-4">
            <RecordAuditTrail tableName="boms" recordId={bom._id} />
          </TabsContent>
        </Tabs>
      </SheetBody>
      <SheetFooter>
        {!bom.isActive ? (
          <>
            <Button variant="ghost" size="icon" onClick={onEdit} aria-label="Edit">
              <Pencil className="size-4" />
            </Button>
            <Button variant="outline" onClick={onClone}>
              <Copy className="h-4 w-4" /> Clone as new version
            </Button>
            <Button onClick={onActivate}>
              <CheckCircle2 className="h-4 w-4" /> Activate this version
            </Button>
          </>
        ) : (
          <Button onClick={onClone}>
            <Copy className="h-4 w-4" /> Clone as new version
          </Button>
        )}
      </SheetFooter>
    </>
  );
}

function DetailItem({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-(--text-tertiary)">
        {label}
      </span>
      <span className="text-[13px] text-(--text-primary)">{children}</span>
    </div>
  );
}
